/**
 * withDelayedClose HOC
 *
 * @author    Nick Dreckshage <ndreckshage@wayfair.com>
 * @author    Frontend Design Systems <FrontendDesignSystems@wayfair.com>
 * @copyright 2019 Wayfair LLC - All rights reserved
 */
import React from 'react';
import PropTypes from 'prop-types';

const INITIAL_STATE = {
  closesAt: null,
  isOpen: false,
  willClose: false,
};

export default function withDelayedClose(WrappedComponent) {
  // eslint-disable-next-line react/no-unsafe
  return class WithDelayedClose extends React.Component {
    static propTypes = {
      closeDelayMs: PropTypes.number,
      isOpen: PropTypes.bool.isRequired,
    };

    static defaultProps = {
      closeDelayMs: 200,
      ...WrappedComponent.defaultProps,
    };

    state = INITIAL_STATE;

    componentDidMount() {
      if (this.props.isOpen) {
        this.open();
      }
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps(nextProps) {
      if (!this.props.isOpen && nextProps.isOpen) {
        this.open();
      } else if (this.props.isOpen && !nextProps.isOpen) {
        this.close();
      }
    }

    closeTimer = null;

    open = () => {
      if (this.state.willClose) {
        clearTimeout(this.closeTimer);
        this.setState({willClose: false});
      } else {
        this.setState({isOpen: true});
      }
    };

    close = () => {
      if (this.props.closeDelayMs > 0) {
        this.closeWithTimeout();
      } else {
        this.closeWithoutTimeout();
      }
    };

    closeWithTimeout = () => {
      const closesAt = Date.now() + this.props.closeDelayMs;
      this.setState({willClose: true, closesAt}, () => {
        this.closeTimer = setTimeout(
          this.closeWithoutTimeout,
          this.state.closesAt - Date.now()
        );
      });
    };

    closeWithoutTimeout = () => this.setState(INITIAL_STATE);

    render() {
      return (
        <WrappedComponent
          {...this.props}
          isOpen={this.state.isOpen}
          willClose={this.state.willClose || !this.state.isOpen}
        />
      );
    }
  };
}
